import add from 'date-fns/add';
import parse from 'date-fns/parse';
import format from 'date-fns/format';
import moment from 'moment';
import { ExpiryDate, Maybe, Scalars } from '../__generated__/pge-types';
import { SupportedLanguages } from '../providers/LanguageProvider';
import { addOrdinalSuffixToNumber, dateSuffixMap } from './format';
import { isAfter, parseISO } from 'date-fns';

export const DATE_FORMAT = 'MM/DD/YYYY';

export const getOneMonthInFuture = (
  value?: Date | string | number | undefined,
): Date => {
  const today: Date = value ? new Date(value) : new Date();
  const futureDate = add(today, { months: 1 });
  return futureDate;
};

export const isExpired = ({ month, year }: ExpiryDate) => {
  const now = new Date(Date.now());
  const expiry = parse(`${month}/${year}`, 'MM/yyyy', now);

  return (
    now.getFullYear() > expiry.getFullYear() ||
    (now.getFullYear() === expiry.getFullYear() &&
      now.getMonth() > expiry.getMonth())
  );
};

export const parseLocalDate = (dateStr: Scalars['LocalDate'], refDate?: Date) =>
  parse(dateStr, 'yyyy-MM-dd', refDate || new Date());

export const formatLocalDate = (date: Date = new Date()) =>
  format(date, 'yyyy-MM-dd');

export const parseTimeRange = (
  timeRange: string,
  date: string,
): { start: string; end: string } | null => {
  const [startStr, endStr] = timeRange.split('-').map(str => str.trim());

  if (!startStr || !endStr) {
    return null;
  }

  const startDate = parse(
    `${date} ${startStr.toUpperCase()}`,
    'MM/dd/yyyy h a',
    new Date(),
  );
  const endDate = parse(
    `${date} ${endStr.toUpperCase()}`,
    'MM/dd/yyyy h a',
    new Date(),
  );

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return null;
  }

  const start = format(startDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX');
  const end = format(endDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX');

  return { start, end };
};

export const getTimeRangeString = (serviceTime: {
  start: string;
  end: string;
}): { date: string; timeRange: string; hourTimeRange: string } | null => {
  const { start, end } = serviceTime;

  const startDate = parse(start, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX', new Date());
  const endDate = parse(end, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX', new Date());

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return null;
  }

  const formattedStartTime = format(startDate, 'h:mm a').toLowerCase();
  const formattedEndTime = format(endDate, 'h:mm a').toLowerCase();

  const formattedHourStartTime = format(startDate, 'h a').toLowerCase();
  const formattedHourEndTime = format(endDate, 'h a').toLowerCase();

  return {
    date: format(startDate, 'MM/dd/yyyy'),
    timeRange: `${formattedStartTime} - ${formattedEndTime}`,
    hourTimeRange: `${formattedHourStartTime} - ${formattedHourEndTime}`,
  };
};

export const formatDateInSpanish = (date: Date) => {
  return new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'long',
  }).format(new Date(date));
};

export const formatDateToLongFormat = (
  date: Date,
  language: SupportedLanguages,
  t: (translationField: string) => string,
) => {
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date string');
  }

  if (language === SupportedLanguages.Spanish) {
    return formatDateInSpanish(date);
  } else {
    const timezoneOffset = date.getTimezoneOffset();
    date.setMinutes(date.getMinutes() + timezoneOffset);
    // Get month name
    const monthOptions: Intl.DateTimeFormatOptions = { month: 'long' };
    const monthName = date.toLocaleDateString('en-US', monthOptions);
    // Format month based on length
    const formattedMonth =
      monthName.length > 4 ? monthName.substring(0, 3) + '.' : monthName;

    // // Format day
    const day = date.getDate();
    const daySuffix = dateSuffixMap(t, day);
    return `${formattedMonth} ${day}${daySuffix}`;
  }
};

export const formatDateToMonth = (date: Date, language: SupportedLanguages) => {
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date string');
  }
  const timezoneOffset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() + timezoneOffset);
  if (language === SupportedLanguages.Spanish) {
    return formatDateInSpanish(date);
  } else {
    // Get month name
    const monthOptions: Intl.DateTimeFormatOptions = { month: 'long' };
    const monthName = date.toLocaleDateString('en-US', monthOptions);

    return monthName;
  }
};

export const formatFullDateWithYear = (
  date: Date,
  language: SupportedLanguages,
  addOrdinalSuffix: boolean = false,
) => {
  const timezoneOffset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() + timezoneOffset);
  const day = date.getDate();
  const year = date.getFullYear();
  const month = formatDateToMonth(date, language);

  return `${month} ${
    addOrdinalSuffix ? addOrdinalSuffixToNumber(day) : day
  }, ${year}`;
};

export const formatFullMonthWithDay = (
  date: Date,
  language: SupportedLanguages,
  t: (translationField: string) => string,
) => {
  const timezoneOffset = date.getTimezoneOffset();
  date.setMinutes(date.getMinutes() + timezoneOffset);
  const day = date.getDate();
  const fullMonth = formatDateToMonth(date, language);
  const daySuffix = dateSuffixMap(t, day);

  return `${fullMonth} ${day}${daySuffix}`;
};

/**
 * Utility to format date string to the specified format
 * @param dateString input date string
 * @param dateFormat input format string or if not provided then default format is MM/DD/YYYY
 * @returns formatted date string
 */
export function formatDateString(
  dateString?: Maybe<string>,
  dateFormat: string = DATE_FORMAT,
): string {
  return dateString ? moment(dateString).format(dateFormat) : '';
}

/**
 * Converts a time slot string into AP style format.
 *
 * This function takes a time slot input (e.g., "10 am - 11 am", "11 a.m. - 2 p.m.") and converts it to AP style,
 * where:
 * - Hours are in numeric format (e.g., "10" instead of "10:00").
 * - AM/PM is written in lowercase with periods (e.g., "a.m." and "p.m.").
 * - If the start and end times have the same period (AM/PM), the period is only included once.
 *
 * Example inputs and outputs:
 * - Input: "10 am - 11 am" → Output: "10 - 11 a.m."
 * - Input: "11 am - 2 pm" → Output: "11 a.m. - 2 p.m."
 * - Input: "10 - 11 a.m." → Output: "10 - 11 a.m."
 * - Input: "11 a.m. - 2 p.m." → Output: "11 a.m. - 2 p.m."
 *
 * @param timeSlot - A string representing the time range, e.g., "10 am - 11 am".
 * @returns A string formatted in AP style.
 */
export function convertTimeToAPStyle(timeSlot: string | undefined): string {
  if (!timeSlot) {
    return '';
  }

  const correctFormatRegex = /^\d{1,2}(:\d{2})?(\s?-\s?\d{1,2}(:\d{2})?)?\s?(a\.m\.|p\.m\.)\s?-\s?\d{1,2}(:\d{2})?\s?(a\.m\.|p\.m\.)$/i;

  if (correctFormatRegex.test(timeSlot.trim())) {
    return timeSlot;
  }

  const [startTime, endTime] = timeSlot.split(' - ').map(t => t.trim());

  const parseTime = (time: string) => {
    const match = time.match(/^(\d{1,2})(:\d{2})?\s?(am|pm|a\.m\.|p\.m\.)?$/i);
    if (!match) {
      throw new Error(`Invalid time format: ${time}`);
    }

    const hour = match[1];
    const minutes = match[2] !== ':00' ? match[2] || '' : '';
    let period = match[3] ? match[3].toLowerCase() : '';

    if (period) {
      period = period.replace(/^am$/i, 'a.m.').replace(/^pm$/i, 'p.m.');
    }

    return { hour, minutes, period };
  };

  const start = parseTime(startTime);
  const end = parseTime(endTime);

  if (!start.period) {
    start.period = end.period;
  }

  if (start.period === end.period) {
    return `${start.hour}${start.minutes} - ${end.hour}${end.minutes} ${start.period}`;
  } else {
    return `${start.hour}${start.minutes} ${start.period} - ${end.hour}${end.minutes} ${end.period}`;
  }
}

//Format UTC timestamp as it is without converting to PST timezone.
//2025-03-17T00:00:00.000Z would return 03/17/2025 00:00:00 instead of 03/16/2025 17:00:00 (PST)
export const getFormattedDateFromUTCSting = (
  date: string | undefined,
  dateFormat = 'MM/dd/yyyy h:mm:ss aa',
): string => {
  if (!date) {
    return 'Invalid Date';
  }

  const parsedDate = parseISO(date);

  // Force UTC time without converting to local timezone
  const utcDate = new Date(
    parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000,
  );

  return format(utcDate, dateFormat);
};
